import { LegalLayout } from "@/components/LegalLayout";

const EMAIL = "ilmuseodellepromesse@gmail.com";

const Section = ({ title, children }) => (
  <div className="space-y-4">
    <h2 className="font-serif text-2xl md:text-3xl text-museo-text mt-10">{title}</h2>
    {children}
  </div>
);

const List = ({ items }) => (
  <ul className="space-y-2 list-none">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3">
        <span className="text-museo-accent">—</span>
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const Mail = () => (
  <a href={`mailto:${EMAIL}`} data-testid="termini-email" className="text-museo-accent hover:text-museo-text transition-colors break-all">
    {EMAIL}
  </a>
);

export default function Termini() {
  return (
    <LegalLayout title="Termini e Condizioni">
      <p className="text-museo-muted/80">Ultimo aggiornamento: 24 giugno 2026</p>
      <p>I presenti Termini e Condizioni regolano l'utilizzo del sito Il Museo delle Promesse.</p>
      <p>
        Utilizzando il sito, acquistando uno spazio o pubblicando una frase, l'utente accetta i presenti
        Termini e Condizioni.
      </p>
      <p>Per qualsiasi domanda, segnalazione o richiesta è possibile contattarci all'indirizzo: <Mail /></p>

      <Section title="1. Descrizione del servizio">
        <p>
          Il Museo delle Promesse è una piattaforma digitale che permette agli utenti di acquistare uno spazio
          all'interno di un museo digitale collettivo e pubblicare una frase breve, anonima o firmata.
        </p>
        <p>
          La frase può essere una promessa, una scusa, un perdono, un addio, una dedica, un pensiero mai detto,
          una confessione o qualsiasi frase personale che l'utente desideri lasciare nel museo, nel rispetto
          delle regole del sito.
        </p>
        <p>Ogni frase pubblicata riceve:</p>
        <List items={[
          "un numero progressivo;",
          "una posizione all'interno del museo digitale;",
          "una pagina pubblica;",
          "la possibilità di essere visualizzata e condivisa tramite link.",
        ]} />
        <p>Il numero massimo previsto è di 100.000 frasi pubblicabili.</p>
      </Section>

      <Section title="2. Natura del servizio">
        <p>Il pagamento effettuato dall'utente serve ad acquistare la pubblicazione di una frase nel museo digitale.</p>
        <p>Il servizio:</p>
        <List items={[
          "non è una donazione;",
          "non è una raccolta fondi;",
          "non è un concorso;",
          "non è una lotteria;",
          "non è un gioco a premi;",
          "non prevede premi, vincite, estrazioni o ricompense economiche;",
          "non promette guadagni o benefici economici all'utente.",
        ]} />
        <p>
          L'utente paga esclusivamente per pubblicare una frase all'interno del sito, secondo le modalità
          previste dai presenti Termini.
        </p>
      </Section>

      <Section title="3. Prezzo del servizio">
        <p>Il prezzo per pubblicare una frase nel Museo delle Promesse è indicato sul sito al momento dell'acquisto.</p>
        <p>Il prezzo iniziale previsto è:</p>
        <p className="font-serif text-xl text-museo-accent">1,99 € per frase pubblicata</p>
        <p>Il prezzo può essere modificato in futuro. Eventuali modifiche non avranno effetto sugli acquisti già completati.</p>
      </Section>

      <Section title="4. Pagamenti">
        <p>I pagamenti vengono gestiti tramite Stripe o altro fornitore di pagamento indicato sul sito.</p>
        <p>Il Museo delle Promesse non conserva direttamente i dati completi della carta di pagamento dell'utente.</p>
        <p>La frase viene pubblicata solo dopo la conferma del pagamento.</p>
        <p>In caso di pagamento non riuscito, annullato o non confermato, la frase non verrà pubblicata.</p>
      </Section>

      <Section title="5. Pubblicazione della frase">
        <p>Dopo il pagamento confermato, la frase dell'utente può essere pubblicata nel museo digitale.</p>
        <p>
          La frase pubblicata potrà essere visibile pubblicamente sul sito e potrà essere accessibile tramite
          una pagina pubblica dedicata.
        </p>
        <p>L'utente comprende e accetta che:</p>
        <List items={[
          "la frase sarà visibile pubblicamente;",
          "altri utenti potranno leggerla;",
          "il link della frase potrà essere condiviso;",
          "la frase potrà essere trovata tramite ricerca interna;",
          "la firma, se inserita, sarà visibile insieme alla frase.",
        ]} />
        <p>L'utente può scegliere di restare anonimo oppure inserire una firma breve opzionale.</p>
      </Section>

      <Section title="6. Firma opzionale">
        <p>La firma è facoltativa.</p>
        <p>L'utente può scegliere di pubblicare la frase come:</p>
        <List items={[
          "anonima;",
          "firmata con nome;",
          "firmata con iniziale;",
          "firmata con soprannome;",
          "firmata con una dedica breve.",
        ]} />
        <p>La firma non deve contenere:</p>
        <List items={[
          "cognomi;",
          "numeri di telefono;",
          "indirizzi email;",
          "link;",
          "dati personali di terzi;",
          "insulti;",
          "bestemmie;",
          "contenuti offensivi;",
          "informazioni private o identificative.",
        ]} />
        <p>Il gestore può rimuovere o modificare la visibilità di firme non conformi alle regole del sito.</p>
      </Section>

      <Section title="7. Responsabilità dell'utente">
        <p>L'utente è l'unico responsabile del contenuto della frase e della firma eventualmente inserita.</p>
        <p>Pubblicando una frase, l'utente dichiara:</p>
        <List items={[
          "di avere il diritto di pubblicare quel contenuto;",
          "che il contenuto non viola diritti di terzi;",
          "che il contenuto non contiene dati personali di altre persone senza consenso;",
          "che il contenuto non è offensivo, diffamatorio, illegale o pericoloso;",
          "che il contenuto rispetta i presenti Termini e Condizioni.",
        ]} />
        <p>
          L'utente si impegna a non utilizzare il sito per arrecare danno, molestare, insultare, minacciare o
          diffamare altre persone.
        </p>
      </Section>

      <Section title="8. Contenuti vietati">
        <p>È vietato pubblicare frasi, firme o contenuti che includano:</p>
        <List items={[
          "bestemmie;", "insulti;", "parolacce pesanti;", "minacce;", "istigazione alla violenza;",
          "contenuti discriminatori;", "odio razziale, religioso, sessuale o politico;", "contenuti diffamatori;",
          "accuse verso persone identificabili;", "dati personali di terzi;", "cognomi;", "indirizzi;",
          "numeri di telefono;", "email;", "link;", "spam;", "pubblicità;", "contenuti sessuali espliciti;",
          "contenuti illegali;", "contenuti che violano diritti di terzi;",
          "contenuti che possano mettere in pericolo l'utente o altre persone.",
        ]} />
        <p>
          Il gestore si riserva il diritto di bloccare, rifiutare, nascondere o rimuovere qualsiasi contenuto
          che ritenga non conforme al progetto, alle regole del sito o alla legge.
        </p>
      </Section>

      <Section title="9. Moderazione">
        <p>Il sito può utilizzare sistemi automatici e controlli manuali per moderare le frasi inviate dagli utenti.</p>
        <p>La moderazione può avvenire:</p>
        <List items={[
          "prima del pagamento;",
          "prima della pubblicazione;",
          "dopo la pubblicazione;",
          "a seguito di segnalazione.",
        ]} />
        <p>
          Il gestore può rifiutare, nascondere o rimuovere una frase in qualsiasi momento se ritiene che violi
          i presenti Termini o possa causare danno, offesa, abuso o problemi legali.
        </p>
        <p>Il pagamento non dà diritto a pubblicare contenuti contrari alle regole del sito.</p>
      </Section>

      <Section title="10. Segnalazioni">
        <p>Gli utenti possono segnalare una frase ritenuta offensiva, illecita, dannosa o non conforme alle regole del sito.</p>
        <p>Le segnalazioni possono essere inviate tramite l'apposita funzione del sito o scrivendo a: <Mail /></p>
        <p>Nella segnalazione è consigliato indicare:</p>
        <List items={[
          "numero della frase;",
          "motivo della segnalazione;",
          "eventuali dettagli utili.",
        ]} />
        <p>Il gestore valuterà la segnalazione e potrà decidere se mantenere, nascondere o rimuovere il contenuto.</p>
      </Section>

      <Section title="11. Rimozione di una frase">
        <p>L'utente può richiedere la rimozione o la modifica della visibilità di una frase scrivendo a: <Mail /></p>
        <p>La richiesta dovrà indicare, se possibile:</p>
        <List items={[
          "numero della frase;",
          "testo della frase;",
          "motivo della richiesta;",
          "eventuali informazioni utili per identificare il contenuto.",
        ]} />
        <p>
          Il gestore valuterà la richiesta compatibilmente con il funzionamento del servizio, la sicurezza del
          sito, gli obblighi di legge e la tutela di eventuali terzi.
        </p>
      </Section>

      <Section title="12. Rimborsi">
        <p>Il servizio consiste nella pubblicazione digitale di una frase all'interno del Museo delle Promesse.</p>
        <p>
          Una volta che la frase è stata pubblicata correttamente, il rimborso non è garantito, salvo obblighi
          inderogabili previsti dalla legge.
        </p>
        <p>Il rimborso potrà essere valutato, ad esempio, in caso di:</p>
        <List items={[
          "doppio pagamento accidentale;",
          "errore tecnico che impedisce la pubblicazione;",
          "pagamento completato senza erogazione del servizio;",
          "altri casi valutati dal gestore.",
        ]} />
        <p>Non sono normalmente previsti rimborsi se:</p>
        <List items={[
          "l'utente cambia idea dopo la pubblicazione;",
          "l'utente ha inserito un contenuto non conforme alle regole;",
          "la frase viene rimossa per violazione dei presenti Termini;",
          "l'utente non ha letto o rispettato le regole del sito.",
        ]} />
        <p>Per richieste di rimborso è possibile scrivere a: <Mail /></p>
      </Section>

      <Section title="13. Diritto di recesso">
        <p>
          Il servizio viene eseguito immediatamente dopo il pagamento, con la pubblicazione della frase nel
          museo digitale.
        </p>
        <p>
          Quando previsto dalla normativa applicabile, l'utente può essere invitato ad accettare espressamente
          l'esecuzione immediata del servizio e a prendere atto che, dopo la pubblicazione della frase, il
          diritto di recesso potrebbe non essere più esercitabile nei casi consentiti dalla legge.
        </p>
        <p>Questa clausola non limita eventuali diritti inderogabili riconosciuti al consumatore dalla normativa applicabile.</p>
      </Section>

      <Section title="14. Disponibilità del servizio">
        <p>Il Museo delle Promesse viene fornito "così com'è" e "secondo disponibilità".</p>
        <p>
          Il gestore si impegna a mantenere il sito funzionante, ma non garantisce che il servizio sarà sempre
          disponibile, continuo, privo di errori o interruzioni.
        </p>
        <p>Il sito potrebbe essere temporaneamente non disponibile per:</p>
        <List items={[
          "manutenzione;", "aggiornamenti;", "problemi tecnici;", "guasti;", "cause di forza maggiore;",
          "interventi di sicurezza;", "problemi dei fornitori esterni.",
        ]} />
        <p>
          Il gestore non è responsabile per eventuali interruzioni temporanee o malfunzionamenti non dipendenti
          direttamente dalla propria volontà.
        </p>
      </Section>

      <Section title="15. Durata della pubblicazione">
        <p>L'obiettivo del Museo delle Promesse è mantenere le frasi visibili nel tempo.</p>
        <p>
          Tuttavia, la pubblicazione "permanente" o "per sempre" deve essere intesa come permanenza all'interno
          del progetto per tutta la durata di esistenza e disponibilità tecnica del servizio.
        </p>
        <p>Il gestore non può garantire l'esistenza eterna del sito, del dominio, del database o dei servizi tecnici collegati.</p>
        <p>Il gestore potrà modificare, sospendere o chiudere il servizio in futuro per motivi tecnici, economici, legali o organizzativi.</p>
      </Section>

      <Section title="16. Proprietà dei contenuti e licenza">
        <p>L'utente mantiene la responsabilità e gli eventuali diritti sul contenuto che pubblica.</p>
        <p>
          Pubblicando una frase, l'utente concede al Museo delle Promesse una licenza gratuita, non esclusiva,
          mondiale e trasferibile per:
        </p>
        <List items={[
          "pubblicare la frase sul sito;",
          "mostrarla all'interno del museo digitale;",
          "renderla accessibile tramite link pubblico;",
          "includerla nella ricerca interna;",
          "visualizzarla in card, anteprime o sezioni del sito;",
          "conservarla nei sistemi tecnici necessari al funzionamento del servizio;",
          "usarla per mostrare, promuovere o raccontare il progetto, sempre nel rispetto dello spirito del sito.",
        ]} />
        <p>Il gestore non è obbligato a utilizzare o mantenere visibile qualsiasi contenuto.</p>
      </Section>

      <Section title="17. Uso corretto del sito">
        <p>L'utente si impegna a non:</p>
        <List items={[
          "tentare di violare la sicurezza del sito;",
          "utilizzare bot, script o sistemi automatici non autorizzati;",
          "aggirare i sistemi di pagamento;",
          "aggirare i sistemi di moderazione;",
          "pubblicare spam;",
          "danneggiare il funzionamento del sito;",
          "usare il sito per attività illegali;",
          "raccogliere dati o contenuti di altri utenti in modo abusivo.",
        ]} />
        <p>Il gestore può bloccare, limitare o rifiutare l'accesso al servizio in caso di uso improprio.</p>
      </Section>

      <Section title="18. Modifiche al servizio">
        <p>Il gestore può modificare, aggiornare, sospendere o interrompere alcune funzioni del sito in qualsiasi momento.</p>
        <p>Potrebbero essere modificati, ad esempio:</p>
        <List items={[
          "grafica;", "funzionalità;", "prezzo;", "modalità di pubblicazione;", "regole di moderazione;",
          "sistemi di pagamento;", "modalità di ricerca;", "struttura delle pagine pubbliche.",
        ]} />
        <p>Le modifiche non annullano automaticamente gli acquisti già completati, salvo casi particolari o obblighi di legge.</p>
      </Section>

      <Section title="19. Limitazione di responsabilità">
        <p>Nei limiti consentiti dalla legge, il gestore non è responsabile per:</p>
        <List items={[
          "contenuti pubblicati dagli utenti;",
          "uso improprio del sito da parte degli utenti;",
          "frasi offensive inserite da terzi;",
          "interruzioni temporanee del servizio;",
          "problemi tecnici di fornitori esterni;",
          "perdita di dati causata da eventi non prevedibili o non controllabili;",
          "danni indiretti, economici o reputazionali derivanti dall'uso del sito.",
        ]} />
        <p>
          Il gestore si impegna comunque a intervenire, nei limiti del possibile, in caso di segnalazioni
          fondate o contenuti non conformi.
        </p>
      </Section>

      <Section title="20. Privacy">
        <p>Il trattamento dei dati personali è regolato dalla Privacy Policy del sito.</p>
        <p>Utilizzando il servizio, l'utente dichiara di aver letto la Privacy Policy.</p>
        <p>Per richieste relative alla privacy è possibile scrivere a: <Mail /></p>
      </Section>

      <Section title="21. Comunicazioni">
        <p>Le comunicazioni relative al servizio possono essere inviate all'indirizzo email: <Mail /></p>
        <p>
          L'utente che contatta il sito è invitato a indicare informazioni sufficienti per comprendere la
          richiesta, come il numero della frase o il motivo della segnalazione.
        </p>
      </Section>

      <Section title="22. Modifiche ai Termini e Condizioni">
        <p>I presenti Termini e Condizioni possono essere aggiornati nel tempo.</p>
        <p>In caso di modifiche, verrà aggiornata la data indicata all'inizio del documento.</p>
        <p>
          L'utilizzo del sito dopo la pubblicazione delle modifiche comporta l'accettazione dei Termini
          aggiornati, nei limiti consentiti dalla legge.
        </p>
      </Section>

      <Section title="23. Legge applicabile">
        <p>
          I presenti Termini e Condizioni sono regolati dalla legge italiana, salvo eventuali diritti
          inderogabili riconosciuti al consumatore dalla normativa applicabile.
        </p>
      </Section>

      <Section title="24. Contatti">
        <p>
          Per qualsiasi domanda, richiesta, segnalazione, rimozione contenuti o comunicazione relativa ai
          presenti Termini e Condizioni, è possibile contattare:
        </p>
        <p>
          Il Museo delle Promesse<br />
          Email: <Mail />
        </p>
      </Section>
    </LegalLayout>
  );
}
